import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Sun, 
  Moon, 
  Monitor, 
  Check,
  Palette
} from 'lucide-react';

const ThemeSelector = () => {
  const { theme, systemTheme, isDark, isSystem, toggleTheme, setLightTheme, setDarkTheme, setSystemThemeMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const themes = [
    {
      id: 'light',
      name: 'Clair',
      icon: Sun,
      description: 'Thème clair pour une utilisation en journée'
    },
    {
      id: 'dark',
      name: 'Sombre',
      icon: Moon,
      description: 'Thème sombre pour une utilisation en soirée'
    },
    {
      id: 'system',
      name: 'Système',
      icon: Monitor,
      description: `Suit les préférences système (${systemTheme === 'dark' ? 'Sombre' : 'Clair'})`
    }
  ];

  const handleThemeChange = (themeId) => {
    switch (themeId) {
      case 'light':
        setLightTheme();
        break;
      case 'dark':
        setDarkTheme();
        break;
      case 'system':
        setSystemThemeMode();
        break;
      default:
        break;
    }
    setIsOpen(false);
  };

  const getCurrentTheme = () => {
    if (isSystem) return themes.find(t => t.id === 'system');
    return themes.find(t => t.id === theme);
  };

  const currentTheme = getCurrentTheme();
  const CurrentIcon = currentTheme?.icon || Sun;

  return (
    <div className="relative">
      {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors duration-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
        title="Changer le thème"
      >
        <CurrentIcon className="w-5 h-5" />
        <span className="hidden sm:inline text-sm font-medium">
          {currentTheme?.name}
        </span>
      </button>

      {/* Menu déroulant */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  Choisir un thème
                </h3>
              </div>
            </div>
            
            <div className="py-2">
              {themes.map((themeOption) => {
                const Icon = themeOption.icon;
                const isSelected = (themeOption.id === 'system' && isSystem) || 
                                 (themeOption.id === theme && !isSystem);
                
                return (
                  <button
                    key={themeOption.id}
                    onClick={() => handleThemeChange(themeOption.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 ${
                      isSelected ? 'bg-primary/10 dark:bg-primary/20' : ''
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      themeOption.id === 'light' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                      themeOption.id === 'dark' ? 'bg-gray-100 dark:bg-gray-700' :
                      'bg-blue-100 dark:bg-blue-900/30'
                    }`}>
                      <Icon className={`w-4 h-4 ${
                        themeOption.id === 'light' ? 'text-yellow-600 dark:text-yellow-400' :
                        themeOption.id === 'dark' ? 'text-gray-600 dark:text-gray-400' :
                        'text-blue-600 dark:text-blue-400'
                      }`} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          isSelected ? 'text-primary dark:text-primary-400' : 'text-gray-900 dark:text-white'
                        }`}>
                          {themeOption.name}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-primary dark:text-primary-400" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {themeOption.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Actions rapides */}
            <div className="p-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={toggleTheme}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200 text-sm font-medium"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                Basculer vers {isDark ? 'clair' : 'sombre'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeSelector;
